/**
 * password-policy.ts — OWASP-aligned password evaluation for Account › Security.
 *
 * Rules follow OWASP ASVS v4.0 §2.1 (Password Security):
 *   2.1.1  minimum 12 characters
 *   2.1.2  at least 64 characters permitted (we cap the UI hint at 64)
 *   2.1.4  all printable Unicode + spaces permitted (no character blocklist)
 *   2.1.7  reject breached / context-specific dictionary passwords
 *   2.1.8  show a password-strength meter
 *   2.1.9  NO composition rules — character-class mixing is advisory only
 *
 * Pure logic, no React, no UI copy: every rule returns a stable `id` the screen
 * maps to a catalog key. `minLength` comes from the account record's policy so
 * a stricter backend policy wins over our ASVS floor.
 *
 * Breach checking is local-only here (a small common-password list). A real
 * k-anonymity HIBP range check is a backend op — tracked as REQ-094.
 */

export type RuleId =
  | "length" | "maxLength" | "notCommon" | "noPersonalInfo" | "noRepeats" | "notCurrent";

export type Rule = { id: RuleId; ok: boolean };

export type Strength = { bits: number; score: 0 | 1 | 2 | 3 | 4 };

export type Evaluation = {
  rules: Rule[];
  /** Advisory character-class variety (ASVS 2.1.9 — never blocking). */
  classes: { lower: boolean; upper: boolean; digit: boolean; symbol: boolean; count: number };
  strength: Strength;
  /** All mandatory rules pass. */
  valid: boolean;
};

export const ASVS_MIN_LENGTH = 12;
export const ASVS_MAX_LENGTH = 64;

/** Top breached/guessable passwords + product-obvious terms. Illustrative only —
 *  the authoritative check belongs on the server (REQ-094). */
const COMMON = [
  "password", "passwort", "123456", "12345678", "123456789", "1234567890", "qwerty", "qwertz",
  "letmein", "welcome", "admin", "administrator", "iloveyou", "monkey", "dragon", "football",
  "sunshine", "princess", "abc123", "111111", "000000", "1q2w3e4r", "qazwsx", "trustno1",
  "changeme", "secret", "starwars", "jrni", "studio", "events", "booking",
];

const SEQUENCES = ["abcdefghijklmnopqrstuvwxyz", "qwertyuiop", "asdfghjkl", "zxcvbnm", "0123456789"];

function hasRun(pw: string): boolean {
  const s = pw.toLowerCase();
  // 3+ of the same character in a row
  if (/(.)\1{2,}/.test(s)) return true;
  // 4+ characters walking a keyboard row / alphabet / digits, forwards or back
  for (const seq of SEQUENCES) {
    const rev = [...seq].reverse().join("");
    for (let i = 0; i + 4 <= seq.length; i++) {
      if (s.includes(seq.slice(i, i + 4))) return true;
      if (s.includes(rev.slice(i, i + 4))) return true;
    }
  }
  return false;
}

function looksCommon(pw: string): boolean {
  const s = pw.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!s) return false;
  return COMMON.some((c) => s === c || (c.length >= 6 && s.includes(c)));
}

function personalTokens(ctx: { email?: string; firstName?: string; lastName?: string }): string[] {
  const out: string[] = [];
  const local = (ctx.email ?? "").split("@")[0] ?? "";
  for (const raw of [local, ctx.firstName ?? "", ctx.lastName ?? ""]) {
    for (const part of raw.split(/[^A-Za-z0-9]+/)) if (part.length >= 4) out.push(part.toLowerCase());
  }
  return out;
}

/** Rough entropy estimate: log2(pool) × length, penalised for runs and
 *  dictionary hits. Deliberately conservative — a meter, not a guarantee. */
function estimate(pw: string, penalised: boolean): Strength {
  if (!pw) return { bits: 0, score: 0 };
  let pool = 0;
  if (/[a-z]/.test(pw)) pool += 26;
  if (/[A-Z]/.test(pw)) pool += 26;
  if (/[0-9]/.test(pw)) pool += 10;
  if (/[^A-Za-z0-9]/.test(pw)) pool += 33;
  if (pool === 0) pool = 26;
  const unique = new Set(pw).size;
  // Repeated characters add less than fresh ones.
  const effective = pw.length * Math.min(1, 0.45 + (unique / pw.length) * 0.55);
  let bits = Math.round(effective * Math.log2(pool));
  if (penalised) bits = Math.round(bits * 0.45);
  const score: Strength["score"] = bits < 40 ? (bits < 28 ? 0 : 1) : bits < 60 ? 2 : bits < 80 ? 3 : 4;
  return { bits, score };
}

export function evaluatePassword(
  pw: string,
  opts: {
    minLength?: number;
    email?: string;
    firstName?: string;
    lastName?: string;
    currentPassword?: string;
  } = {},
): Evaluation {
  const min = Math.max(ASVS_MIN_LENGTH, opts.minLength ?? ASVS_MIN_LENGTH);
  const lower = /[a-z]/.test(pw), upper = /[A-Z]/.test(pw);
  const digit = /[0-9]/.test(pw), symbol = /[^A-Za-z0-9]/.test(pw);
  const tokens = personalTokens(opts);
  const haystack = pw.toLowerCase();
  const common = looksCommon(pw);
  const runs = hasRun(pw);

  const rules: Rule[] = [
    { id: "length", ok: pw.length >= min },
    { id: "maxLength", ok: pw.length <= ASVS_MAX_LENGTH },
    { id: "notCommon", ok: pw.length > 0 && !common },
    { id: "noPersonalInfo", ok: pw.length > 0 && !tokens.some((tk) => haystack.includes(tk)) },
    { id: "noRepeats", ok: pw.length > 0 && !runs },
    { id: "notCurrent", ok: pw.length > 0 && (!opts.currentPassword || pw !== opts.currentPassword) },
  ];

  return {
    rules,
    classes: { lower, upper, digit, symbol, count: [lower, upper, digit, symbol].filter(Boolean).length },
    strength: estimate(pw, common || runs),
    valid: rules.every((r) => r.ok),
  };
}
